import http, { getAccessToken } from './http';

const ENDPOINTS = {
  PATIENTS: '/api/v1/patients',
  PATIENT_BY_ID: (id) => `/api/v1/patients/${id}`,
  PATIENT_PHOTO: (id) => `/api/v1/patients/${id}/photo`,
  PATIENT_STATS: '/api/v1/patients/stats',
  FAMILIES: '/api/v1/families',
  FAMILY_BY_ID: (id) => `/api/v1/families/${id}`,
  FAMILY_SEARCH: '/api/v1/families/search',
  FAMILY_JOIN: '/api/v1/families/join',
  FAMILY_LEAVE: (patientId) => `/api/v1/families/leave/${patientId}`,
};

/**
 * Patient API Service
 */
export const patientAPI = {
  // Create a new patient
  create: async (patientData) => {
    const response = await http.post(ENDPOINTS.PATIENTS, patientData);
    return response.data;
  },

  // Create patient with photo upload (multipart/form-data)
  createWithPhoto: async (patientData, patientPhoto = null, vitalsData = null) => {
    // Create FormData for multipart/form-data submission
    const formData = new FormData();
    
    // Add basic patient data
    formData.append('firstName', patientData.firstName);
    formData.append('lastName', patientData.lastName);
    if (patientData.mobile) formData.append('mobile', patientData.mobile);
    if (patientData.email) formData.append('email', patientData.email);
    if (patientData.sex) formData.append('sex', patientData.sex);
    if (patientData.dob) formData.append('dob', patientData.dob);
    if (patientData.bloodGroup) formData.append('bloodGroup', patientData.bloodGroup);
    if (patientData.medicalNotes) formData.append('medicalNotes', patientData.medicalNotes);

    // Insurance Details
    if (patientData.policyNumber) formData.append('policyNumber', patientData.policyNumber);
    if (patientData.certificateNumber) formData.append('certificateNumber', patientData.certificateNumber);
    if (patientData.companyTpaId) formData.append('companyTpaId', patientData.companyTpaId);
    if (patientData.abhaNumber) formData.append('abhaNumber', patientData.abhaNumber);
    if (patientData.insuranceCommencementDate) formData.append('insuranceCommencementDate', patientData.insuranceCommencementDate);
    if (patientData.sumInsured) formData.append('sumInsured', patientData.sumInsured);
    
    // Add medical history fields
    if (patientData.pastHistory) formData.append('pastHistory', patientData.pastHistory);
    if (patientData.medicalHistory) formData.append('medicalHistory', patientData.medicalHistory);
    
    // Add drug allergies array
    if (patientData.drugAllergies && Array.isArray(patientData.drugAllergies) && patientData.drugAllergies.length > 0) {
      patientData.drugAllergies.forEach((allergy, index) => {
        formData.append(`drugAllergies[${index}]`, allergy);
      });
    }
    
    // Add allergies array
    if (patientData.allergies && Array.isArray(patientData.allergies) && patientData.allergies.length > 0) {
      patientData.allergies.forEach((allergy, index) => {
        formData.append(`allergies[${index}]`, allergy);
      });
    }
    
    // Add current medications array
    if (patientData.currentMedications && Array.isArray(patientData.currentMedications) && patientData.currentMedications.length > 0) {
      patientData.currentMedications.forEach((medication, index) => {
        formData.append(`currentMedications[${index}]`, medication);
      });
    }
    
    // Add address as nested object
    if (patientData.personalAddress) {
      Object.entries(patientData.personalAddress).forEach(([key, value]) => {
        if (value) {
          formData.append(`personalAddress[${key}]`, value);
        }
      });
    }

    // Add office details
    if (patientData.officeDetail) {
      const office = patientData.officeDetail;
      const officeDetailString = [office.company, office.designation, office.officeAddress]
        .filter(Boolean)
        .join(', ');
      if (officeDetailString) formData.append('officeDetail', officeDetailString);
    }
    
    // Only include assignedDoctor if it's a valid ObjectId
    if (patientData.assignedDoctor && 
        patientData.assignedDoctor.length === 24 && 
        /^[0-9a-fA-F]{24}$/.test(patientData.assignedDoctor)) {
      formData.append('assignedDoctor', patientData.assignedDoctor);
    }

    // Add photo if provided
    if (patientPhoto) {
      formData.append('photo', patientPhoto);
    }

    // Add vitals data if provided
    if (vitalsData) {
      if (vitalsData.bloodPressure?.systolic) formData.append('vitals[bloodPressure][systolic]', vitalsData.bloodPressure.systolic);
      if (vitalsData.bloodPressure?.diastolic) formData.append('vitals[bloodPressure][diastolic]', vitalsData.bloodPressure.diastolic);
      if (vitalsData.heartRate) formData.append('vitals[heartRate]', vitalsData.heartRate);
      if (vitalsData.temperature) formData.append('vitals[temperature]', vitalsData.temperature);
      if (vitalsData.respiratoryRate) formData.append('vitals[respiratoryRate]', vitalsData.respiratoryRate);
      if (vitalsData.oxygenSaturation) formData.append('vitals[oxygenSaturation]', vitalsData.oxygenSaturation);
      if (vitalsData.height) formData.append('vitals[height]', vitalsData.height);
      if (vitalsData.weight) formData.append('vitals[weight]', vitalsData.weight);
      if (vitalsData.notes) formData.append('vitals[notes]', vitalsData.notes);
    }

    // Use http instance for consistent authentication handling
    const response = await http.post(ENDPOINTS.PATIENTS, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  // Get patient by ID
  getById: async (id) => {
    const response = await http.get(ENDPOINTS.PATIENT_BY_ID(id));
    return response.data;
  },

  // Update patient
  update: async (id, patientData) => {
    // Backend route uses PUT for updates
    const response = await http.put(ENDPOINTS.PATIENT_BY_ID(id), patientData);
    return response.data;
  },

  // List patients with filtering
  list: async (params = {}) => {
    const response = await http.get(ENDPOINTS.PATIENTS, { params });
    return response.data;
  },

  // Search patients - accepts params object (e.g., { mobile: '123' } or { search: 'name' })
  search: async (params = {}) => {
    const response = await http.get(ENDPOINTS.PATIENTS, { params });
    return response.data;
  },

  // Search patients by term (for UI components)
  searchPatients: async (searchTerm) => {
    if (!String(searchTerm).trim()) {
      return { data: { patients: [] } };
    }

    const response = await http.get(ENDPOINTS.PATIENTS, { 
      params: { 
        search: searchTerm,
        limit: 20 // Limit results for UI performance
      } 
    });
    
    console.log('🔧 Raw API response in searchPatients:', response);
    
    // The backend returns: { success: true, message: '...', data: { patients: [...], pagination: {...} } }
    // So we need to access response.data.data.patients
    const patients = response.data?.data?.patients || response.data?.patients || [];
    
    console.log('🔧 Extracted patients in API:', patients);
    
    return {
      data: {
        patients: patients
      }
    };
  },

  // Get patient statistics
  getStats: async () => {
    const response = await http.get(ENDPOINTS.PATIENT_STATS);
    return response.data;
  },

  // Delete patient
  delete: async (id) => {
    const response = await http.delete(ENDPOINTS.PATIENT_BY_ID(id));
    return response.data;
  },

  // Upload or update patient photo
  uploadPhoto: async (id, photoFile) => {
    const formData = new FormData();
    formData.append('photo', photoFile);

    const response = await http.post(ENDPOINTS.PATIENT_PHOTO(id), formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  }
};

/**
 * Family API Service
 */
export const familyAPI = {
  // Create a new family
  create: async (familyData) => {
    const response = await http.post(ENDPOINTS.FAMILIES, familyData);
    return response.data;
  },

  // Get family by ID
  getById: async (id) => {
    const response = await http.get(ENDPOINTS.FAMILY_BY_ID(id));
    return response.data;
  },

  // Update family
  update: async (id, familyData) => {
    const response = await http.put(ENDPOINTS.FAMILY_BY_ID(id), familyData);
    return response.data;
  },

  // List families
  list: async (params = {}) => {
    const response = await http.get(ENDPOINTS.FAMILIES, { params });
    return response.data;
  },

  // Search families
  search: async (searchParams) => {
    const response = await http.get(ENDPOINTS.FAMILY_SEARCH, { params: searchParams });
    return response.data;
  },

  // Join an existing family
  join: async (joinData) => {
    const response = await http.post(ENDPOINTS.FAMILY_JOIN, joinData);
    return response.data;
  },

  // Leave family
  leave: async (patientId) => {
    const response = await http.delete(ENDPOINTS.FAMILY_LEAVE(patientId), {
      data: { confirm: true }
    });
    return response.data;
  },

  // Delete family
  delete: async (id) => {
    const response = await http.delete(ENDPOINTS.FAMILY_BY_ID(id));
    return response.data;
  }
};

export default { patientAPI, familyAPI };
